import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User } from "../../lib/common/types/user";
import { OnBoardingStatus } from "lib/common/types/onBoardingStatus";
import { IUserSubscription } from "lib/common/types/userSubscription";
import type { RootState } from "../store";
import { getFromLocalStorage } from "lib/common/helper/LocalStorage";
import parseJwt from "utils/helper/jwtParser";

const registerToken: any =
  typeof window !== "undefined" ? getFromLocalStorage("registerToken") : null;

interface UserState {
  isLoggedIn: boolean;
  // False until the initial "am I logged in" check (withTryLoggingIn's
  // useGetMe call) has actually finished, one way or the other. Pages
  // that gate their UI on isLoggedIn alone were rendering the logged-out
  // state on first paint every time, since isLoggedIn's own initial value
  // is false -- that's the "login page flashes, then the real page"
  // symptom. Check this first and show a neutral loading state while
  // it's false, instead of trusting isLoggedIn before it's meaningful.
  authChecked: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  user: User | null;
  onBoardingStatus: OnBoardingStatus | null;
  subscriptionsStatus: IUserSubscription[];
  subscriptionsLoaded: boolean;
}
const initialState: UserState = {
  isLoggedIn: false,
  authChecked: false,
  isAdmin: false,
  isSuperAdmin: false,
  user: registerToken
    ? { userData: { mobile: parseJwt(registerToken).mobile } }
    : null,
  onBoardingStatus: null,
  subscriptionsStatus: [],
  subscriptionsLoaded: false,
};
export const userSlice = createSlice({
  name: "user",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    login: (
      state,
      action: PayloadAction<{
        user: User;
        isAdmin?: boolean;
        isSuperAdmin?: boolean;
      }>
    ) => ({
      ...state,
      isLoggedIn: true,
      authChecked: true,
      user: action.payload.user,
      isSuperAdmin: action.payload.isSuperAdmin ?? false,
      isAdmin: action.payload.isAdmin ?? false,
    }),
    forgotPassword: (
      state,
      action: PayloadAction<{
        user: User;
      }>
    ) => ({
      ...state,
      user: action.payload.user,
    }),
    register: (
      state,
      action: PayloadAction<{ user: User; onBoardingStatus: OnBoardingStatus }>
    ) => ({
      ...state,
      user: action.payload.user,
      onBoardingStatus: action.payload.onBoardingStatus,
    }),
    onBoardUser: (
      state,
      action: PayloadAction<{
        user: User;
        isAdmin?: boolean;
        isSuperAdmin?: boolean;
        onBoardingStatus: OnBoardingStatus;
      }>
    ) => ({
      ...state,
      isLoggedIn: true,
      authChecked: true,
      user: action.payload.user,
      isSuperAdmin: action.payload.isSuperAdmin ?? false,
      isAdmin: action.payload.isAdmin ?? false,
      onBoardingStatus: action.payload.onBoardingStatus,
    }),
    updateOnboardStatus: (
      state,
      action: PayloadAction<{ onBoardingStatus: OnBoardingStatus }>
    ) => ({
      ...state,
      onBoardingStatus: action.payload.onBoardingStatus,
    }),
    updateUser: (state, action: PayloadAction<{ user: User }>) => ({
      ...state,
      user: action.payload.user,
    }),
    updateEmailVerificationStatus: (
      state,
      action: PayloadAction<{ emailVerified: boolean }>
    ) => ({
      ...state,
      user: { ...state.user, emailVerified: action.payload.emailVerified },
    }),
    updateMobile: (state, action: PayloadAction<{ mobile: string }>) => ({
      ...state,
      user: {
        ...state.user,
        userData: {
          ...state?.user?.userData,
          mobile: action.payload.mobile,
        },
      },
    }),
    updateSubscribeStatus: (
      state,
      action: PayloadAction<{ subscriptionsStatus: any }>
    ) => ({
      ...state,
      subscriptionsStatus: action.payload.subscriptionsStatus,
      subscriptionsLoaded: true,
    }),

    logout: (state) => ({
      ...state,
      isLoggedIn: false,
      authChecked: true,
      isSuperAdmin: false,
      isAdmin: false,
      user: null,
      onBoardingStatus: null,
      subscriptionsStatus: [],
      subscriptionsLoaded: false,
    }),
    // Dispatched once useGetMe's check has definitively resolved to "not
    // logged in" -- login/onBoardUser already set authChecked themselves
    // for the "is logged in" outcome.
    markAuthChecked: (state) => ({
      ...state,
      authChecked: true,
    }),
  },
});

export const {
  login,
  onBoardUser,
  logout,
  register,
  updateOnboardStatus,
  forgotPassword,
  updateUser,
  updateEmailVerificationStatus,
  updateMobile,
  updateSubscribeStatus,
  markAuthChecked,
} = userSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const isLoggedIn = (state: RootState) => state.user.isLoggedIn;
export const getAuthChecked = (state: RootState) => state.user.authChecked;
export const isAdmin = (state: RootState) => state.user.isAdmin;
export const isSuperAdmin = (state: RootState) => state.user.isSuperAdmin;
export const getUser = (state: RootState) => state.user.user;
export const getOnBoardingStatus = (state: RootState) =>
  state.user.onBoardingStatus;
export const getSubscriptionStatus = (state: RootState) =>
  state.user.subscriptionsStatus;
export const getSubscriptionsLoaded = (state: RootState) =>
  state.user.subscriptionsLoaded;

export default userSlice.reducer;
